import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';

import { TRADE_STATUS } from '../../tradeBenefitsConstants.ts';

import { type TradeBenefitsHeroStatusProgressBar_rewards$key } from './__generated__/TradeBenefitsHeroStatusProgressBar_rewards.graphql.ts';

const COLORS_BY_PROGRAM: Record<string, { bg: string; fill: string }> = {
    [TRADE_STATUS.BRONZE]: { bg: '#FEF4EA', fill: '#C6996C' },
    [TRADE_STATUS.SILVER]: { bg: '#F0F0F0', fill: '#A2A2A2' },
    [TRADE_STATUS.GOLD]: { bg: '#FAF6EB', fill: '#EBCE89' },
    [TRADE_STATUS.PLATINUM]: { bg: '#F0F4F8', fill: '#B5C6DB' },
};

const rewardsFragment = graphql`
    fragment TradeBenefitsHeroStatusProgressBar_rewards on TradeFirmRewards {
        pointsToMaintainStatus
        currentYearSpend {
            amount
        }
        pointsNeededToLevelUp
        currentProgram {
            programInfo {
                code
            }
        }
    }
`;

const getPercentage = (currentYearSpend: number, pointsRemaining: number): number => {
    const totalToNextLevel = currentYearSpend + pointsRemaining;

    if (totalToNextLevel <= 0) {
        return 0;
    }

    return (currentYearSpend / totalToNextLevel) * 100;
};

const TradeBenefitsHeroStatusProgressBar: FC<{
    rewards: TradeBenefitsHeroStatusProgressBar_rewards$key | null | undefined;
}> = ({ rewards: rewardsRef }) => {
    const rewards = useFragment(rewardsFragment, rewardsRef);
    const programCode = rewards?.currentProgram?.programInfo?.code;
    const currentYearSpend = rewards?.currentYearSpend?.amount || 0;
    const pointsRemaining =
        programCode === TRADE_STATUS.PLATINUM
            ? Number(rewards?.pointsToMaintainStatus) || 0
            : Number(rewards?.pointsNeededToLevelUp) || 0;
    const percentage = getPercentage(currentYearSpend, pointsRemaining);
    const clampedPercentage = Math.max(0, Math.min(100, percentage));
    const colors = COLORS_BY_PROGRAM[programCode || ''] || COLORS_BY_PROGRAM[TRADE_STATUS.BRONZE];

    return (
        <div className={dibsCss.myXsmall}>
            <div
                className={classnames(
                    dibsCss.border,
                    dibsCss.borderSolid,
                    dibsCss.border2,
                    dibsCss.roundedFull,
                    dibsCss.overflowHidden,
                    dibsCss.wFull
                )}
                style={{ backgroundColor: colors.bg, borderColor: colors.fill, height: 14 }}
            >
                <div
                    className={classnames(dibsCss.roundedFull, dibsCss.hFull)}
                    style={{ backgroundColor: colors.fill, width: `${clampedPercentage}%` }}
                />
            </div>
        </div>
    );
};

export { TradeBenefitsHeroStatusProgressBar };
